import React from 'react';
import { useNavigate } from 'react-router-dom';
import ensnaredAwardPhoto from '../assets/ensnared-award.jpg';
import ensnaredCardsSheet from '../assets/ensnared-cards.png';
import ensnaredGamePhoto from '../assets/ensnared-game.jpg';
import ensnaredPrintPhoto from '../assets/ensnared-print.jpg';
import { projectDetailThemeStyles } from '../projectThemes';
import './ProjectDetailLayout.css';
import './EnsnaredProject.css';

export const EnsnaredProject: React.FC = () => {
  const navigate = useNavigate();
  return (
    <main
      className="project-detail-page ensnared-project-page"
      style={projectDetailThemeStyles.ensnared}
    >
      <aside className="project-page__sidebar">
        <button type="button" className="project-page__back" onClick={() => navigate(-1)}>
          <span className="project-page__back-arrow" aria-hidden="true">
            &lt;-
          </span>
          back to showcase
        </button>
        <div className="project-page__sidebar-summary">
          <p className="project-page__eyebrow">game jam / board game design</p>
          <h1
            className="project-page__title"
            aria-label="Ensnared: A Board Game Built in 48 Hours at KiwiJam"
          >
            Ensnared
          </h1>
          <p className="project-page__description">
            A physical board game about escaping a spider&rsquo;s web, designed and
            built in one weekend at KiwiJam Auckland 2026 &mdash; winner of the
            most-complete award.
          </p>
          <div className="project-page__tech-list" aria-label="Project disciplines">
            <span>Board game design</span>
            <span>Card design</span>
            <span>3D printing</span>
          </div>
        </div>
      </aside>

      <section className="project-page__content" aria-label="Project content">
        <div className="project-page__section">
          <p className="project-page__section-kicker">kiwijam auckland 2026 / case study</p>
          <h2 className="project-page__section-title">
            Escape the web before the spider wakes.
          </h2>
          <div className="project-page__section-body">
            <p>
              KiwiJam gives you about 48 hours to build a game from scratch around a
              theme revealed at kick-off. Ours was <em>Entanglement</em> &mdash; so we
              made a board game where everyone is tangled in a spider&rsquo;s web,
              strung with real thread.
            </p>
            <p>
              Players start at the Spider Nest with three lives, racing to escape
              before the spider claims them. Roll the spider face and the web stirs:
              spider tokens drag threads across your path while a shared Hunger Scale
              creeps toward the red.
            </p>
            <figure className="project-page__inline-figure project-page__inline-figure--small">
              <img
                className="project-page__inline-image"
                src={ensnaredGamePhoto}
                alt="The Ensnared board mid-game: a web-shaped board with 3D-printed spider tokens trailing yarn threads, dice, meeples, and the spider nest centrepiece"
              />
            </figure>
            <ul className="ensnared-project__facts" aria-label="Project facts">
              <li>
                <span className="ensnared-project__facts-label">Event</span>
                <span>KiwiJam Auckland 2026, Unleash Space, University of Auckland</span>
              </li>
              <li>
                <span className="ensnared-project__facts-label">Theme</span>
                <span>Entanglement</span>
              </li>
              <li>
                <span className="ensnared-project__facts-label">Format</span>
                <span>Physical board game, built in about 48 hours</span>
              </li>
              <li>
                <span className="ensnared-project__facts-label">Award</span>
                <span>&ldquo;Completey-ey&rdquo; &mdash; the most-complete award</span>
              </li>
            </ul>
            <p>
              I designed the item deck &mdash; twenty-odd cards tuned to give players
              just enough agency without making escape safe. My favourite rule:
              Broken Runes are worthless alone but open a portal in pairs, turning
              card-trading into a tense little economy.
            </p>
            <figure className="project-page__inline-figure">
              <img
                className="project-page__inline-image"
                src={ensnaredCardsSheet}
                alt="The five Ensnared item cards: Rope, Lantern, Knife, Red Potion, and Broken Rune"
              />
            </figure>
            <p>
              The pieces were meant to be paper, thumbtacks, and spare blocks. I
              didn&rsquo;t want the game&rsquo;s villain to be a pushpin &mdash; so I
              spent the night modelling and printing a full spider set on a Bambu Lab
              A1 mini: small, support-free prints, iterated between playtests.
            </p>
            <figure className="project-page__inline-figure project-page__inline-figure--small">
              <img
                className="project-page__inline-image"
                src={ensnaredPrintPhoto}
                alt="A Bambu Lab A1 mini mid-print, surrounded by freshly printed spider tokens and the spider-nest centrepiece"
              />
            </figure>
            <p>
              Each spider sits on a spool base the web thread ties onto, so moving a
              thread is one smooth pull. And the most-praised touch: red pigment on
              the nest spider&rsquo;s fangs, freshly fed &mdash; people leaned in to
              check whether it came out of the printer that way.
            </p>
            <p>
              By Sunday, Ensnared was playable end to end &mdash; board, deck, thread,
              pieces, rulebook &mdash; and took home the most-complete award, engraved{' '}
              <em>&ldquo;completey-ey&rdquo;</em>.
            </p>
            <figure className="project-page__inline-figure project-page__inline-figure--small">
              <img
                className="project-page__inline-image"
                src={ensnaredAwardPhoto}
                alt="The engraved acrylic KiwiJam '26 trophy reading completey-ey, held up at the venue"
              />
            </figure>
          </div>
        </div>
      </section>
    </main>
  );
};
